import { createFileRoute, Link } from "@tanstack/react-router";
import { IsolatedFlag } from "@/components/system/IsolatedFlag";
import { useEffect, useMemo } from "react";
import { Sparkles, Flame, Trophy } from "lucide-react";
import { resolveTakeover } from "@/lib/influencer/influencerTakeover";
import { WorldStatusBanner } from "@/components/global/WorldStatusBanner";
import { GlobalEnergyBar } from "@/components/global/GlobalEnergyBar";
import { NationRivalryWidget } from "@/components/social/NationRivalryWidget";
import { FomoTicker } from "@/components/social/FomoTicker";
import { CommunityEnergyTicker } from "@/components/ambassadors/CommunityEnergyTicker";
import { ShareEverywhereGrid } from "@/components/share/ShareEverywhereGrid";
import { CountdownPulse } from "@/components/countdown/CountdownPulse";
import { useDemoCountdownPhase } from "@/lib/countdown/worldCupCountdown";
import { usePersistedCreatorSource } from "@/lib/ambassadors/creatorLinks";
import { WorldCupTagline } from "@/components/brand/WorldCupTagline";
import { CinematicBrandIntro } from "@/components/brand/CinematicBrandIntro";
import { StadiumAmbientLayer } from "@/components/stadium/StadiumAmbientLayer";
import { FeaturedFansPanel } from "@/components/social/FeaturedFansPanel";
// audit tracking omitted — event type not registered; landing pageview suffices.

/**
 * Viral landing flow — /wc/<slug>.
 * Slug can be a nation (mexico, brasil, argentina), a creator preset
 * (juancito), or a campaign keyword (worldcup, nationwars). Anything
 * unknown falls back to a generic World Cup hype landing.
 */
export const Route = createFileRoute("/wc/$slug")({
  head: ({ params }) => {
    const t = resolveTakeover(params.slug);
    const title = t.active
      ? `${t.name} — VerseSport World Cup`
      : "VerseSport — World Cup Hype Live";
    const desc = t.active
      ? `${t.slogan} · Vive el Mundial con hype global, nation wars y momentos cinematográficos.`
      : "Hype global, nation wars y momentos cinematográficos del Mundial — en vivo.";
    const url = `https://versesport.com/wc/${params.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: ViralLandingPage,
});

function ViralLandingPage() {
  const { slug } = Route.useParams();
  const takeover = useMemo(() => resolveTakeover(slug), [slug]);
  const phase = useDemoCountdownPhase();
  usePersistedCreatorSource();

  useEffect(() => {
    if (typeof window !== "undefined") {
      try { window.console.info("[viral-landing]", slug); } catch { /* noop */ }
    }
  }, [slug]);

  const accent = takeover.accent ?? "#E94560";
  const shareUrl = typeof window !== "undefined"
    ? window.location.href
    : `https://versesport.com/wc/${slug}`;
  const shareMsg = takeover.active
    ? `${takeover.emoji} ${takeover.slogan}`
    : `🔥 Vive el Mundial con VerseSport`;

  return (
    <main
      className="relative mx-auto max-w-3xl space-y-5 px-4 py-6"
      data-viral-landing={slug}
    >
      <StadiumAmbientLayer />
      <CinematicBrandIntro sessionKey={`vs:cinematic_intro_wc_${slug}`} />
      <header
        className="pitch-team-frame relative overflow-hidden rounded-3xl border border-border/60 p-5"
        style={{ background: `linear-gradient(135deg, ${accent}33, transparent 70%)` }}
      >
        <div className="pitch-team-ambient" aria-hidden />
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
            World Cup Live
          </span>
          <div className="ml-auto"><CountdownPulse phase={phase} /></div>
        </div>
        <h1 className="mt-3 text-3xl font-black leading-tight">
          {takeover.active ? takeover.name : "VerseSport · Mundial en vivo"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {takeover.active ? takeover.slogan : "Hype global, nation wars y momentos cinematográficos."}
        </p>
        <WorldCupTagline className="mt-3" size="xs" align="left" withProduct />
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to="/showcase"
            className="rounded-xl bg-primary px-4 py-2 text-xs font-black uppercase tracking-wider text-primary-foreground"
          >
            <Flame className="mr-1 inline h-3.5 w-3.5" /> Ver el showcase
          </Link>
          <Link
            to="/ambassadors"
            className="rounded-xl border border-border/60 bg-background/70 px-4 py-2 text-xs font-black uppercase tracking-wider"
          >
            <Trophy className="mr-1 inline h-3.5 w-3.5" /> {takeover.challengeLabel ?? "Únete al ejército"}
          </Link>
        </div>
      </header>

      <FomoTicker compact />
      <IsolatedFlag name="WorldStatusBanner" flag="overlays"><WorldStatusBanner /></IsolatedFlag>
      <IsolatedFlag name="GlobalEnergyBar" flag="overlays"><GlobalEnergyBar /></IsolatedFlag>
      <NationRivalryWidget />

      <FeaturedFansPanel limit={5} />

      <section className="rounded-2xl border border-border/60 bg-background/60 p-3">
        <h2 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">
          Comparte el hype
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Lleva esta landing a tu comunidad — WhatsApp, X, TikTok, Reels, Discord.
        </p>
        <div className="mt-3">
          <ShareEverywhereGrid url={shareUrl} message={shareMsg} />
        </div>
      </section>

      <div className="flex justify-center">
        <CommunityEnergyTicker />
      </div>
    </main>
  );
}